import moment from "moment";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, Redirect, useParams } from "react-router-dom";

import Alert from "veneer/Alert";
import Box from "veneer/Box";
import Button from "veneer/Button";
import Stack from "veneer/Stack";

import { FETCH_NETWORK_DEVICE_VERSIONS } from "cortex/ui/src/fleet/redux-store/ui-state/network-device-versions/actions";
import {
  getAgentVersionOptions,
  getPlayerVersionOptions,
} from "cortex/ui/src/fleet/redux-store/ui-state/network-device-versions/selectors";
import { Application } from "cortex/ui/src/fleet/view/application/model";
import { getStrategyContentAppId } from "cortex/ui/src/fleet/view/strategy/utils";
import { useAppDispatch, useAppSelector } from "cortex/ui/src/hooks";
import { FormData, Network } from "cortex/ui/src/network/model";
import { createPromiseApiAction } from "cortex/ui/src/redux-store/api-sagas/actions";
import {
  applicationsSelector,
  applicationsStoresLoadedSelector,
  getAllByIdExceptSchedulers,
  getSingleZoneSchedulerAppId,
  installedAppsSelector,
} from "cortex/ui/src/redux-store/applications/selectors";
import { getAuthUser } from "cortex/ui/src/redux-store/auth/selectors";
import { EDIT_NETWORK } from "cortex/ui/src/redux-store/networks/actions";
import {
  isSystemManaged,
  networksSelector,
  networksStoresLoadedSelector,
} from "cortex/ui/src/redux-store/networks/selectors";
import { makeGetAllAsArray } from "cortex/ui/src/redux-store/selectors";
import { isReadonly } from "cortex/ui/src/redux-store/users/selectors";
import BackButton from "cortex/ui/src/ui/button";
import { NavLoader } from "cortex/ui/src/ui/loader";
import Notification from "cortex/ui/src/ui/notification";
import { Option } from "cortex/ui/src/ui/table/types";
import { getBreadcrumbs } from "cortex/ui/src/util/breadcrumbs";
import {
  type ResponseLike,
  getErrorMessage,
} from "cortex/ui/src/util/response";
import { Container, Panel, StickyPageActions } from "cortex/ui/src/view/layout";

import EditNetworkForm from "./EditNetworkForm";

const DAYS = Object.freeze([
  "Disable",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
  "Sunday",
  "Every day",
]);

const getAllAppsAsArray = makeGetAllAsArray();

interface NetworkDetailsPayload {
  agentVersion: string | null;
  day: number;
  disableRemoteUpdates: boolean;
  fallbackContents: string;
  hour: number;
  mainApp: string;
  networkName: string;
  operateParams: string;
  playerVersion: string | null;
  sandboxStatus: boolean;
}

interface ServiceArgs {
  networkDetails: NetworkDetailsPayload;
  networkId: number;
}

interface VersionResponse {
  data: {
    data: {
      versions?: {
        agent?: ReadonlyArray<string>;
        player?: ReadonlyArray<string>;
      };
    };
  };
}

interface RouteParams {
  id: string;
}

function getDefaultNetworkDetails(): FormData {
  return {
    agentVersion: null,
    day: 0,
    deviceOperateTime: "",
    deviceUpdateDisabled: true,
    fallbackContents: [],
    hasAutoRestartSchedule: false,
    hasSystemStrategy: false,
    hour: 0,
    networkName: "",
    playerVersion: null,
    sandboxStatus: true,
    selectedApplication: null,
  };
}

function getNetworkDetails(
  network: Network | undefined,
  applications: ReadonlyArray<Application>,
): FormData {
  const details = getDefaultNetworkDetails();

  if (!network) {
    return details;
  }

  const {
    agent_version: agentVersion,
    device_restart_time: deviceRestartTime,
    player_version: playerVersion,
  } = network;

  if (agentVersion) {
    details.agentVersion = {
      value: agentVersion,
      label: agentVersion,
    };
  }

  if (playerVersion) {
    details.playerVersion = {
      value: playerVersion,
      label: playerVersion,
    };
  }

  details.deviceUpdateDisabled = network.device_update_disabled;
  details.deviceOperateTime = network.device_operate_time ?? "";
  details.networkName = network.name;
  details.sandboxStatus = network.is_sandbox;

  if (deviceRestartTime) {
    details.hasAutoRestartSchedule = true;
    if (deviceRestartTime.startsWith("*")) {
      const time = moment(deviceRestartTime, "*-H");
      if (time.isValid()) {
        details.day = 8;
        details.hour = time.hour();
      }
    } else {
      const time = moment(deviceRestartTime, "E-H");
      if (time.isValid()) {
        details.day = time.isoWeekday();
        details.hour = time.hour();
      }
    }
  }

  const appId = getStrategyContentAppId(network);
  if (appId) {
    const application = applications.find((app) => app.id === appId);
    if (application) {
      details.selectedApplication = {
        value: application.id,
        label: application.display_name,
      };
    }
    details.fallbackContents = network.strategy_content?.priorities?.[1] ?? [];
    details.hasSystemStrategy = true;
  }

  return details;
}

function getHourOptions(lastHour: number): ReadonlyArray<Option<number>> {
  return Array.from(Array(lastHour), (_el, value) => {
    return {
      value,
      label: `${String(value).padStart(2, "0")}:00`,
    };
  });
}

function validateSystemStrategy(formData: FormData): string {
  if (!formData.hasSystemStrategy) {
    return "";
  }

  const hasApp = Boolean(formData.selectedApplication);
  const hasFallback = formData.fallbackContents.length > 0;

  if (!hasApp && !hasFallback) {
    return "You must select an app and at least one fallback creative for your network to play. To require this setting at the device level instead, turn Content Source off.";
  }

  if (!hasApp && hasFallback) {
    return "You must select an app for your network to play.";
  }

  if (hasApp && !hasFallback) {
    return "At least one fallback creative must be selected.";
  }

  return "";
}

function getNetworkDetailsPayload(
  formData: FormData,
  schedulerAppId: number,
): NetworkDetailsPayload {
  const {
    agentVersion,
    day,
    deviceOperateTime,
    deviceUpdateDisabled,
    fallbackContents,
    hasAutoRestartSchedule,
    hasSystemStrategy,
    hour,
    networkName,
    playerVersion,
    sandboxStatus,
    selectedApplication,
  } = formData;

  return {
    day: hasAutoRestartSchedule ? day : 0,
    fallbackContents: hasSystemStrategy ? JSON.stringify(fallbackContents) : "",
    hour,
    mainApp:
      selectedApplication && hasSystemStrategy
        ? JSON.stringify({
            appId: selectedApplication.value,
            scheduler: schedulerAppId,
          })
        : "",
    networkName,
    sandboxStatus,
    agentVersion: agentVersion ? agentVersion.value : null,
    playerVersion: playerVersion ? playerVersion.value : null,
    disableRemoteUpdates: deviceUpdateDisabled,
    operateParams: deviceOperateTime,
  };
}

function getApplicationOptions(
  applications: ReadonlyArray<Application>,
): ReadonlyArray<Option<number>> {
  return Object.values(applications)
    .map((app) => ({
      label: app.display_name,
      value: app.id,
    }))
    .sort((a, b) => a.label.localeCompare(b.label));
}

export default function NetworkEdit() {
  const { id } = useParams<RouteParams>();
  const dispatch = useAppDispatch();
  const applicationsStoresLoaded = useAppSelector(
    applicationsStoresLoadedSelector,
  );
  const networks = useAppSelector(networksSelector);
  const network = networks[id] as Network | undefined;
  const networksStoresLoaded = useAppSelector(networksStoresLoadedSelector);
  const storesLoaded = useMemo(
    () => applicationsStoresLoaded && networksStoresLoaded,
    [applicationsStoresLoaded, networksStoresLoaded],
  );
  const user = useAppSelector(getAuthUser);
  const readonly = isReadonly(user);
  const allAppsById = useAppSelector(applicationsSelector);
  const appById = getAllByIdExceptSchedulers({
    byId: allAppsById,
  });
  const schedulerAppId = getSingleZoneSchedulerAppId(allAppsById) ?? 132;
  const apps = getAllAppsAsArray(
    appById,
  ) as unknown as ReadonlyArray<Application>;
  const applications = installedAppsSelector(
    apps,
  ) as ReadonlyArray<Application>;
  const initialFormData = useMemo(
    () => getNetworkDetails(network, applications),
    [network, applications],
  );
  const [agentVersionOptions, setAgentVersionOptions] = useState<
    ReadonlyArray<Option<string>>
  >([]);
  const [playerVersionOptions, setPlayerVersionOptions] = useState<
    ReadonlyArray<Option<string>>
  >([]);
  const [errorMessage, setErrorMessage] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [formData, setFormData] = useState<FormData>(initialFormData);
  const previousNetworkId = useRef<number | undefined>(network?.id);
  const previousApplicationCount = useRef(applications.length);

  const dayOptions = useMemo(
    () => DAYS.map((day, index) => ({ label: day, value: index })),
    [],
  );
  const hourOptions = useMemo(() => getHourOptions(24), []);
  const applicationOptions = useMemo(
    () => getApplicationOptions(applications),
    [applications],
  );

  useEffect(() => {
    if (network?.id !== previousNetworkId.current) {
      setFormData(getNetworkDetails(network, applications));
      previousNetworkId.current = network?.id;
    }
  }, [network, applications]);

  useEffect(() => {
    const applicationsLoaded =
      applications.length > 0 && previousApplicationCount.current === 0;

    if (applicationsLoaded) {
      const details = getNetworkDetails(network, applications);
      setFormData((prev) => ({
        ...prev,
        selectedApplication: details.selectedApplication,
        fallbackContents: details.fallbackContents,
      }));
    }

    previousApplicationCount.current = applications.length;
  }, [network, applications]);

  useEffect(() => {
    if (!network) {
      return;
    }

    let cancelled = false;

    (async () => {
      try {
        const res = (await createPromiseApiAction(
          dispatch,
          FETCH_NETWORK_DEVICE_VERSIONS,
          [network.id],
          "",
        )) as VersionResponse;
        const { versions = {} } = res.data.data;
        const agentVersions = versions.agent
          ? Array.from(versions.agent)
          : undefined;
        const playerVersions = versions.player
          ? Array.from(versions.player)
          : undefined;

        if (!cancelled) {
          setAgentVersionOptions(
            getAgentVersionOptions({
              agent: agentVersions,
              player: playerVersions,
            }),
          );
          setPlayerVersionOptions(
            getPlayerVersionOptions({
              agent: agentVersions,
              player: playerVersions,
            }),
          );
        }
      } catch (e) {
        if (!cancelled) {
          setErrorMessage(getErrorMessage(e as Error | ResponseLike | null));
          setSuccessMessage("");
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [dispatch, network]);

  const updateNetworkDetails = (serviceArgs: ServiceArgs) => {
    return createPromiseApiAction(
      dispatch,
      EDIT_NETWORK,
      [serviceArgs.networkId, serviceArgs.networkDetails],
      "",
    );
  };

  const handleSubmit = async () => {
    if (!network) {
      return;
    }

    setSubmitted(true);
    const validationMessage = validateSystemStrategy(formData);

    if (validationMessage) {
      window.scrollTo(0, 0);
      setErrorMessage(validationMessage);
      setSubmitted(false);
      return;
    }

    try {
      await updateNetworkDetails({
        networkId: network.id,
        networkDetails: getNetworkDetailsPayload(formData, schedulerAppId),
      });
      setErrorMessage("");
      setSuccessMessage("Network details updated.");
    } catch (e) {
      setErrorMessage(getErrorMessage(e as Error | ResponseLike | null));
      setSuccessMessage("");
    } finally {
      setSubmitted(false);
      window.scrollTo(0, 0);
    }
  };

  const renderBreadcrumbs = () => {
    if (!network) {
      return undefined;
    }

    const path = `/networks/${network.id}`;

    return getBreadcrumbs([
      { label: "Networks", target: "/networks" },
      { label: network.name, target: path },
    ]);
  };

  const renderView = () => {
    if (!storesLoaded) {
      return <NavLoader.Proxy />;
    }

    if (!network) {
      return <Redirect to="/404" />;
    }

    return (
      <>
        <Notification
          error={errorMessage}
          success={successMessage}
          successChildren={
            <Link to={`/networks/${network.id}`}>View Network</Link>
          }
        />
        <EditNetworkForm
          agentVersionOptions={agentVersionOptions}
          applicationOptions={applicationOptions}
          dayOptions={dayOptions}
          errors={{}}
          hourOptions={hourOptions}
          onChange={setFormData}
          playerVersionOptions={playerVersionOptions}
          readonly={readonly}
          systemManaged={isSystemManaged(network)}
          value={formData}
        />
        <StickyPageActions>
          <Stack alignItems="center" direction="row" gap={1}>
            <BackButton />
            <Button
              disabled={submitted}
              loading={submitted}
              onClick={handleSubmit}
            >
              Save
            </Button>
          </Stack>
        </StickyPageActions>
      </>
    );
  };

  const name = network ? network.name : "Network";
  const systemManaged = network ? isSystemManaged(network) : false;

  return (
    <Container
      breadcrumbs={renderBreadcrumbs() ?? undefined}
      hideBox
      p={2}
      title={`Edit ${name}`}
    >
      <Panel spacing={2}>
        {systemManaged && (
          <Box mb={2}>
            <Alert dataTestId="ad-platform-sync-alert" dense severity="info">
              This network is synced with the Ad Platform, therefore certain
              fields will not be editable.
            </Alert>
          </Box>
        )}
        {renderView()}
      </Panel>
    </Container>
  );
}

export {
  getDefaultNetworkDetails,
  getNetworkDetails,
  getNetworkDetailsPayload,
  validateSystemStrategy,
};
